import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { UnitContentCard } from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { CONTROL_TYPE_ALARM } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'

export interface IControlAlarmStateFormProps {
  stateName: string
  alarmPartition: string
}

interface IProps {
  loading: boolean
  disabled: boolean
}

export const ControlAlarmStatesForm: React.FC<IProps> = ({ loading, disabled }) => {
  const { register, control, watch, formState } = useFormContext()
  const controlTypeId = watch('controlTypeId')
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alarmStates',
    rules: {
      required: false,
      validate: (value) => {
        if (controlTypeId === CONTROL_TYPE_ALARM && value?.length === 0) {
          return 'Alarm controls require at least one partition.'
        }

        return true
      },
      minLength: 1
    }
  })

  const rootError = formState?.errors?.alarmStates?.root?.message as string
  const errors = formState?.errors?.alarmStates as any

  if (controlTypeId === CONTROL_TYPE_ALARM) {
    return (
      <UnitContentCard classes={classNames('relative bg-surface border border-border', rootError && '!border-error')}>
        <div className='pl-[10px] flex items-center justify-between'>
          <SectionTitle text='Partitions' />
          <ButtonRoundSm
            type='button'
            className='action-success'
            loading={loading || disabled}
            callback={() =>
              append({
                stateName: '',
                alarmPartition: ''
              })
            }
          >
            <commonIcons.add />
          </ButtonRoundSm>
        </div>
        <motion.div>
          <AnimatePresence mode='popLayout' initial={false}>
            {fields.map((field: any, index) => (
              <motion.div
                key={`${field.id}.alarmStates.container`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <VertSpacerMd />
                <UnitContentCard classes='bg-surface-2'>
                  <div className='flex items-center justify-end'>
                    <ButtonRoundSm
                      type='button'
                      className='action-warning'
                      loading={loading || disabled}
                      callback={() => {
                        remove(index)
                      }}
                    >
                      <commonIcons.remove />
                    </ButtonRoundSm>
                  </div>
                  <div className='flex flex-col gap-[15px]'>
                    <InputSM
                      key={`${field.id}.stateName`}
                      type='text'
                      placeholder='Partition name'
                      label='Partition name'
                      id={`alarmStates.${index}.stateName`}
                      showLabel
                      loading={loading}
                      disabled={disabled}
                      registration={register(`alarmStates.${index}.stateName`, {
                        required: FORM_DEFAULT_REQUIRED
                      })}
                      errors={(errors as any)?.[index]?.stateName}
                    />
                    <InputSM
                      key={`${field.id}.alarmPartition`}
                      type='text'
                      placeholder='0 to 99'
                      label='Partition number'
                      id={`alarmStates.${index}.alarmPartition`}
                      showLabel
                      loading={loading}
                      disabled={disabled}
                      registration={register(`alarmStates.${index}.alarmPartition`, {
                        required: FORM_DEFAULT_REQUIRED,
                        pattern: {
                          value: /^[0-9]{2}$/,
                          message: '2 numerical characters only e.g. 01'
                        }
                      })}
                      errors={(errors as any)?.[index]?.alarmPartition}
                    />
                  </div>
                </UnitContentCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {rootError && <p className='pl-[10px] text-[0.8em] text-error jost'>{rootError}</p>}
      </UnitContentCard>
    )
  }

  return null
}
